import React, { useEffect, useRef, useState } from 'react';
import { easeOutCubic, shouldAnimate } from '../../utils/motion';

const format = (value) => Math.round(value).toLocaleString('es-MX');

/**
 * Renders the final number (so static HTML is correct) and, on the client,
 * counts up from zero the first time it scrolls into view.
 */
export default function CountUp({ value, delay = 0, duration = 1300 }) {
  const ref = useRef(null);
  const [display, setDisplay] = useState(format(value));

  useEffect(() => {
    const element = ref.current;
    if (!element || !shouldAnimate() || !('IntersectionObserver' in window)) return undefined;

    let frame = 0;
    let timer = 0;

    const run = () => {
      setDisplay('0');
      timer = window.setTimeout(() => {
        const start = performance.now();
        const tick = (now) => {
          const progress = Math.min(1, (now - start) / duration);
          setDisplay(format(value * easeOutCubic(progress)));
          if (progress < 1) frame = window.requestAnimationFrame(tick);
        };
        frame = window.requestAnimationFrame(tick);
      }, delay);
    };

    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      observer.disconnect();
      run();
    });
    observer.observe(element);

    return () => {
      observer.disconnect();
      window.clearTimeout(timer);
      window.cancelAnimationFrame(frame);
    };
  }, [value, delay, duration]);

  return <span ref={ref}>{display}</span>;
}
